import { Divider, Stack, Typography } from '@mui/material';
import { useState, useMemo, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';

type Translate = ReturnType<typeof useTranslation>['t'];
import { AlignedDraggableRow } from '../DrawerDraggableLayout';
import CollapsibleDraggableData from '../CollapsibleDraggableData';
import SearchBar from '../SearchBar';
import DashboardDrawerTabDescription from '../../DashboardDrawerTabDescription';
import { createDefaultFieldSelection } from '../../../../utils/fieldSelection';
import type { WidgetOptions } from '../../dashboardTypes';

export interface DraggableWidgetDefinition {
  id: string;
  type: string;
  title: string;
  dataKey: string;
  minH?: number;
  minW?: number;
  options?: WidgetOptions;
}

export interface DrivebaseWidgetGroupConfig {
  id: string;
  /** When true, first widget is the row header and the rest expand under a chevron. */
  collapsible: boolean;
  widgets: DraggableWidgetDefinition[];
}

export function flattenDrivebaseDrawerWidgets(groups: DrivebaseWidgetGroupConfig[]): DraggableWidgetDefinition[] {
  return groups.flatMap((group) => group.widgets);
}

export function buildDrivebaseWidgetGroups(t: Translate): DrivebaseWidgetGroupConfig[] {
  return [
    {
      id: 'imu-attitude',
      collapsible: true,
      widgets: [
        {
          id: 'IMU Attitude Sensor Data',
          type: 'imu',
          dataKey: 'all',
          minH: 3,
          minW: 4,
          title: String(t('dashboard.draggableComponent.title.imu', 'Orientation Data')),
        },
        {
          id: 'IMU Attitude Pitch',
          type: 'imu',
          dataKey: 'Pitch',
          minW: 4,
          title: t('dashboard.draggableComponent.title.imu.pitch', 'Orientation Data Pitch'),
        },
        {
          id: 'IMU Attitude Roll',
          type: 'imu',
          dataKey: 'Roll',
          minW: 4,
          title: t('dashboard.draggableComponent.title.imu.roll', 'Orientation Data Roll'),
        },
        {
          id: 'IMU Attitude Yaw',
          type: 'imu',
          dataKey: 'Yaw',
          minW: 4,
          title: t('dashboard.draggableComponent.title.imu.yaw', 'Orientation Data Yaw'),
        },
      ],
    },
    {
      id: 'gyroscope',
      collapsible: true,
      widgets: [
        {
          id: 'Gyroscope Sensor Data',
          type: 'gyro',
          dataKey: 'all',
          minH: 3,
          minW: 4,
          title: String(t('dashboard.draggableComponent.title.gyro', 'Gyroscope Data')),
        },
        {
          id: 'Gyroscope Pitch',
          type: 'gyro',
          dataKey: 'Gyro X',
          minW: 4,
          title: t('dashboard.draggableComponent.title.gyro.pitch', 'Gyroscope Data Pitch'),
        },
        {
          id: 'Gyroscope Roll',
          type: 'gyro',
          dataKey: 'Gyro Y',
          minW: 4,
          title: t('dashboard.draggableComponent.title.gyro.roll', 'Gyroscope Data Roll'),
        },
        {
          id: 'Gyroscope Yaw',
          type: 'gyro',
          dataKey: 'Gyro Z',
          minW: 4,
          title: t('dashboard.draggableComponent.title.gyro.yaw', 'Gyroscope Data Yaw'),
        },
      ],
    },
    {
      id: 'accelerometer',
      collapsible: true,
      widgets: [
        {
          id: 'Accelerometer Sensor Data',
          type: 'accel',
          dataKey: 'all',
          minH: 3,
          minW: 4,
          title: String(t('dashboard.draggableComponent.title.accel', 'Accelerometer Data')),
        },
        {
          id: 'Accelerometer Sensor Data X',
          type: 'accel',
          dataKey: 'Accel X',
          minW: 4,
          title: t('dashboard.draggableComponent.title.accel.x', 'Accelerometer Data X'),
        },
        {
          id: 'Accelerometer Sensor Data Y',
          type: 'accel',
          dataKey: 'Accel Y',
          minW: 4,
          title: t('dashboard.draggableComponent.title.accel.y', 'Accelerometer Data Y'),
        },
        {
          id: 'Accelerometer Sensor Data Z',
          type: 'accel',
          dataKey: 'Accel Z',
          minW: 4,
          title: t('dashboard.draggableComponent.title.accel.z', 'Accelerometer Data Z'),
        },
      ],
    },
    {
      id: 'velocity',
      collapsible: true,
      widgets: [
        {
          id: 'Velocity Sensor Data',
          type: 'vel',
          dataKey: 'all',
          minH: 3,
          minW: 4,
          title: String(t('dashboard.draggableComponent.title.vel', 'Velocity Data')),
        },
        {
          id: 'Velocity Sensor Data Total',
          type: 'vel',
          dataKey: 'Velocity Magnitude',
          minW: 4,
          title: t('dashboard.draggableComponent.title.vel.total', 'Velocity Data Total'),
        },
        {
          id: 'Velocity Sensor Data X',
          type: 'vel',
          dataKey: 'Velocity X',
          minW: 4,
          title: t('dashboard.draggableComponent.title.vel.x', 'Velocity Data X'),
        },
        {
          id: 'Velocity Sensor Data Y',
          type: 'vel',
          dataKey: 'Velocity Y',
          minW: 4,
          title: t('dashboard.draggableComponent.title.vel.y', 'Velocity Data Y'),
        },
      ],
    },
    {
      id: 'distance',
      collapsible: false,
      widgets: [
        {
          id: 'Linear Distance Sensor Data',
          type: 'dist',
          dataKey: 'all',
          minH: 3,
          minW: 4,
          title: String(t('dashboard.draggableComponent.title.dist', 'Linear distance')),
        },
      ],
    },
    {
      id: 'orientation',
      collapsible: false,
      widgets: [
        {
          id: 'Orientation Widget',
          type: 'orientation',
          dataKey: 'all',
          minH: 2,
          minW: 2,
          title: t('dashboard.draggableComponent.title.drive-unit-orientation', 'Drive Unit Orientation'),
        },
        {
          id: 'Field Widget',
          dataKey: 'all',
          type: 'field',
          options: { fieldType: createDefaultFieldSelection() },
          title: t('dashboard.draggableComponent.title.field', 'Field Widget'),
        },
      ],
    },
    {
      id: 'position',
      collapsible: false,
      widgets: [
        {
          id: 'Position Sensor Data',
          type: 'pos',
          dataKey: 'all',
          minH: 3,
          minW: 4,
          title: String(t('dashboard.draggableComponent.title.pos', 'Position Data')),
        },
      ],
    },
    {
      id: 'speed',
      collapsible: false,
      widgets: [
        {
          id: 'Speed Widget',
          type: 'speed',
          dataKey: 'Velocity Magnitude',
          minH: 2,
          minW: 2,
          title: t('dashboard.draggableComponent.title.speed', 'Speed'),
        },
      ],
    },
  ];
}

function DrivebaseWidgetGroupRows({ group }: { group: DrivebaseWidgetGroupConfig }) {
  if (group.collapsible) {
    return <CollapsibleDraggableData data={group.widgets} />;
  }

  return (
    <Stack alignItems="center" spacing={1}>
      {group.widgets.map((item) => (
        <AlignedDraggableRow key={item.id} widget={item} />
      ))}
    </Stack>
  );
}

const DrivebaseDataContent = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const widgetGroups = useMemo(() => buildDrivebaseWidgetGroups(t), [t]);

  const filterWidgets = useCallback(
    (widgets: DraggableWidgetDefinition[]) => {
      if (!searchQuery) return widgets;
      return widgets.filter((widget) => widget.title.toLowerCase().includes(searchQuery));
    },
    [searchQuery]
  );

  const filteredGroups = useMemo(
    () => widgetGroups.map((group) => ({ ...group, widgets: filterWidgets(group.widgets) })).filter((group) => group.widgets.length > 0),
    [widgetGroups, filterWidgets]
  );

  const hasWidgetResults = filteredGroups.length > 0;

  return (
    <Stack direction="column" width="100%" spacing={1} sx={{ p: 2 }} alignItems={'stretch'}>
      <Stack direction="row" spacing={0} alignItems={'center'} sx={{ width: '100%' }}>
        <DashboardDrawerTabDescription>
          <Trans i18nKey="dashboard.drawer.drivebaseData.description">
            Drive unit sensor data. Drag widgets to the dashboard to visualize data during program execution.
          </Trans>
        </DashboardDrawerTabDescription>
      </Stack>

      <SearchBar onSearch={handleSearch} />

      {hasWidgetResults && (
        <>
          <Divider sx={{ width: '100%', mb: 1 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            <Trans i18nKey="dashboard.draggableComponent.section.imu">IMU</Trans>
          </Typography>
          {filteredGroups.map((group) => (
            <DrivebaseWidgetGroupRows key={group.id} group={group} />
          ))}
        </>
      )}

      {!hasWidgetResults && searchQuery && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
          <Trans i18nKey="dashboard.draggableComponent.no-widgets-found" values={{ searchQuery }}>
            No widgets found matching "{{ searchQuery }}"
          </Trans>
        </Typography>
      )}
    </Stack>
  );
};

export default DrivebaseDataContent;
